// src/components/trial/lp/trial-beta/sections/games.tsx
// Ported from the design attachment, markup lines 4014-4043. The `.pt-game-track` doubled-list
// marquee is the same shape as the sibling carousel already shipped for other LP variants
// (`lp/v2b/components/interactive/game-carousel.tsx`, and its v2a/v2c/v3b twins): a list rendered
// twice back to back, scrolled by a CSS keyframe that translates it exactly -50% so the seam is
// invisible. Unlike that sibling, the keyframe itself (`tb-pt-scroll-games`) lives in
// `trial-beta.css` alongside `.pt-game-track`'s animation/hover rules, not injected at runtime:
// keyframe names are global to the document, and Task 3 established the `tb-` prefix convention
// for every keyframe on this page precisely so sections ported in later tasks (8, 9, ...) don't
// collide with each other. Covers come from `TRIAL_BETA_ASSETS` instead of the sibling's
// hardcoded paths, per the brief.
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import { TRIAL_BETA_ASSETS } from '@/components/trial/lp/trial-beta/assets';

interface GameEntry {
  name: string;
  src: string;
}

// Game titles are proper nouns — not translated, matching the sibling carousels' convention.
const GAMES: readonly GameEntry[] = [
  { name: 'Valorant', src: TRIAL_BETA_ASSETS.gameValorant },
  { name: 'CS2', src: TRIAL_BETA_ASSETS.gameCs2 },
  { name: 'Fortnite', src: TRIAL_BETA_ASSETS.gameFortnite },
  { name: 'Dota 2', src: TRIAL_BETA_ASSETS.gameDota2 },
  { name: 'World of Warcraft', src: TRIAL_BETA_ASSETS.gameWow },
  { name: 'Final Fantasy XIV', src: TRIAL_BETA_ASSETS.gameFfxiv },
  { name: 'Apex Legends', src: TRIAL_BETA_ASSETS.gameApex },
  { name: 'League of Legends', src: TRIAL_BETA_ASSETS.gameLol },
];

function GameCard({ name, src }: GameEntry) {
  return (
    <div className='mx-4 w-[180px] shrink-0'>
      <div className='flex flex-col items-center gap-3 overflow-hidden rounded-2xl border bg-[#0D0D12] p-4 transition-all duration-300 hover:scale-105 hover:border-[#93FF18]/40' style={{ borderColor: '#93FF1820' }}>
        <div className='relative h-[200px] w-[150px] overflow-hidden rounded-xl'>
          <Image src={src} alt={name} fill className='object-cover' sizes='150px' />
        </div>
        <span className='w-full truncate text-center text-sm font-semibold text-white/90' style={{ fontFamily: 'var(--font-outfit)' }}>
          {name}
        </span>
      </div>
    </div>
  );
}

export function Games() {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const track = [...GAMES, ...GAMES];

  return (
    <section id='games' className='relative px-4 py-14 md:px-8 md:py-20' style={{ zIndex: 1 }}>
      <div className='pointer-events-none absolute inset-0' aria-hidden='true' style={{ background: 'linear-gradient(to bottom, transparent, #93FF1805, transparent)' }} />
      <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
        <div className='mb-10 text-center md:mb-14'>
          <span className='mb-3 block text-xs font-semibold uppercase tracking-[0.3em] md:text-sm' style={{ color: '#93FF18', fontFamily: 'var(--font-outfit)' }}>
            {t('games.eyebrow')}
          </span>
          <h2 className='mb-4 text-3xl font-bold md:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
            {t('games.title')} <span style={{ color: '#93FF18' }}>{t('games.titleHighlight')}</span>
          </h2>
          <p className='mx-auto max-w-2xl text-base md:text-lg' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
            {t('games.description')}
          </p>
        </div>
        <div className='relative w-full overflow-hidden py-4' style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}>
          <div data-anim='games-marquee' className='pt-game-track flex items-center'>
            {track.map((game, trackIndex) => (
              <GameCard key={`${game.name}-${trackIndex}`} name={game.name} src={game.src} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
